import { useAppStore } from '../store';
import { motion } from 'framer-motion';
import { AlertTriangle, CheckCircle, ArrowRight, RefreshCw, BookOpen } from 'lucide-react';

export default function MistakesPage() {
  const { mistakes, activeSubjectId, toggleMistakeResolved } = useAppStore();
  const subjectMistakes = mistakes.filter(m => m.question.subjectId === activeSubjectId);
  const unresolved = subjectMistakes.filter(m => !m.resolved);
  const resolved = subjectMistakes.filter(m => m.resolved);

  return (
    <div className="max-w-5xl mx-auto space-y-6 sm:space-y-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display text-stone-950 tracking-tight">错题本</h1>
        <p className="text-sm text-stone-400 mt-2 font-body">典型错题归纳，分析错误原因，掌握正确解法</p>
      </motion.div>

      {/* Stats */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="grid grid-cols-2 gap-3 sm:gap-4">
        <div className="bg-terracotta-50 rounded-2xl p-4 sm:p-5 border border-terracotta-light/20">
          <p className="text-xs text-terracotta font-body">待解决</p>
          <p className="text-3xl font-display text-stone-950 mt-1">{unresolved.length}</p>
        </div>
        <div className="bg-sage-50 rounded-2xl p-4 sm:p-5 border border-sage-light/20">
          <p className="text-xs text-sage-dark font-body">已解决</p>
          <p className="text-3xl font-display text-stone-950 mt-1">{resolved.length}</p>
        </div>
      </motion.div>

      {/* Mistake entries */}
      <div className="space-y-4">
        {subjectMistakes.map((mistake, i) => (
          <motion.div
            key={mistake.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 + i * 0.06 }}
            className={`card-premium overflow-hidden ${mistake.resolved ? 'opacity-70' : ''}`}
          >
            {/* Header */}
            <div className={`px-4 sm:px-5 py-2.5 sm:py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 ${mistake.resolved ? 'bg-sage-50' : 'bg-terracotta-50'}`}>
              <div className="flex items-center gap-2">
                {mistake.resolved ? (
                  <CheckCircle className="w-4 h-4 text-sage-dark" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-terracotta" />
                )}
                <span className="text-xs font-semibold text-stone-950 font-body">
                  {mistake.resolved ? '已解决' : '待解决'} · {mistake.date}
                </span>
              </div>
              <button
                onClick={() => toggleMistakeResolved(mistake.id)}
                className={`text-[10px] px-3.5 py-1.5 rounded-xl font-medium transition-all font-body ${
                  mistake.resolved
                    ? 'bg-white text-stone-500 hover:bg-stone-100 border border-stone-200'
                    : 'bg-sage text-white hover:bg-sage-dark'
                }`}
              >
                {mistake.resolved ? '标记未解决' : '标记已解决'}
              </button>
            </div>

            <div className="p-4 sm:p-5 space-y-3 sm:space-y-4">
              <div>
                <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-1.5 font-body">原题</p>
                <p className="text-sm text-stone-950 leading-relaxed whitespace-pre-line font-body">{mistake.question.content}</p>
              </div>

              <div className="bg-terracotta-50 rounded-xl p-4">
                <p className="text-[10px] font-semibold text-terracotta mb-1.5 font-body">你的回答</p>
                <p className="text-xs text-stone-600 font-body">{mistake.studentAnswer}</p>
              </div>

              <div className="bg-cedar-50 rounded-xl p-4">
                <p className="text-[10px] font-semibold text-cedar mb-1.5 flex items-center gap-1 font-body">
                  <AlertTriangle className="w-3 h-3" /> 错误原因分析
                </p>
                <p className="text-xs text-stone-600 leading-relaxed font-body">{mistake.errorReason}</p>
              </div>

              <div className="bg-sage-50 rounded-xl p-4">
                <p className="text-[10px] font-semibold text-sage-dark mb-1.5 flex items-center gap-1 font-body">
                  <CheckCircle className="w-3 h-3" /> 正确解法
                </p>
                <p className="text-xs text-stone-600 leading-relaxed font-body">{mistake.correctApproach}</p>
              </div>

              {mistake.similarQuestions.length > 0 && (
                <div className="border-t border-stone-100 pt-4">
                  <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-2.5 flex items-center gap-1 font-body">
                    <RefreshCw className="w-3 h-3" /> 同类巩固题
                  </p>
                  {mistake.similarQuestions.map(sq => (
                    <div key={sq.id} className="p-3.5 rounded-xl bg-steel-50 text-xs text-stone-600 font-body">
                      {sq.content}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
